// src/components/Admin/FirmPortalUsers.js
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableContainer, TableHead, TableRow, TableCell,
    TableBody, CircularProgress, Alert, Chip, Button, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl,
    InputLabel, Select, MenuItem, Grid, Avatar, FormHelperText
} from '@mui/material';
import {
    PersonAdd as PersonAddIcon,
    Edit as EditIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    AdminPanelSettings as AdminIcon,
    Person as PersonIcon,
    PersonOff as PersonOffIcon,
    Email as EmailIcon,
    Business as BusinessIcon
} from '@mui/icons-material';
import { db, functions } from '../../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';

const FirmPortalUsers = ({ user, orgData, orgConfig }) => {
    // Data state
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Modal state
    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Form state
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [inviteRole, setInviteRole] = useState('user');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Terminology
    const fcfoLabel = orgConfig?.terminology?.fcfo || 'FCFO';
    const fcfosLabel = orgConfig?.terminology?.fcfos || 'FCFOs';

    // Fetch users
    useEffect(() => {
        const fetchUsers = async () => {
            if (!user?.organizationId) {
                setError("No organization ID found.");
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                const usersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', user.organizationId)
                );
                const usersSnap = await getDocs(usersQuery);
                
                // Fetch client counts for each user
const userList = await Promise.all(usersSnap.docs.map(async (docSnap) => {
    const userData = docSnap.data();
    
    // Query clients with organizationId to satisfy security rules
    const clientsQuery = query(
        collection(db, 'clients'),
        where('fcfoId', '==', docSnap.id),
        where('organizationId', '==', user.organizationId),
        where('isActive', '==', true)
    );
    const clientsSnap = await getDocs(clientsQuery);
    
    return {
        id: docSnap.id,
        ...userData,
        clientCount: clientsSnap.size
    };
}));

                // Sort: admins first, then by name
                userList.sort((a, b) => {
                    if (a.firmPortalRole === 'admin' && b.firmPortalRole !== 'admin') return -1;
                    if (a.firmPortalRole !== 'admin' && b.firmPortalRole === 'admin') return 1;
                    return (a.name || a.email || '').localeCompare(b.name || b.email || '');
                });

                setUsers(userList);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching users:', err);
                setError(`Failed to load users: ${err.message}`);
                setLoading(false);
            }
        };

        fetchUsers();
    }, [user?.organizationId, refreshTrigger]);

    // Handle invite user
    const handleInviteUser = async () => {
        if (!inviteEmail.trim()) {
            toast.error('Email is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const firmPortalInviteUser = httpsCallable(functions, 'firmPortalInviteUser');
            const result = await firmPortalInviteUser({
                email: inviteEmail.trim(),
                name: inviteName.trim(),
                firmPortalRole: inviteRole,
                organizationId: user.organizationId
            });

            if (result.data.success) {
                toast.success(result.data.message || 'User invited successfully');
                setInviteModalOpen(false);
                setInviteEmail('');
                setInviteName('');
                setInviteRole('user');
                setRefreshTrigger(prev => prev + 1);
            } else {
                throw new Error(result.data.error || 'Failed to invite user');
            }
        } catch (err) {
            console.error('Error inviting user:', err);
            toast.error(err.message || 'Failed to invite user');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle update user role
    const handleUpdateRole = async (targetUser, newRole) => {
        try {
            const firmPortalSetUserRole = httpsCallable(functions, 'firmPortalSetUserRole');
            const result = await firmPortalSetUserRole({
                userId: targetUser.id,
                newRole: newRole
            });

            if (result.data.success) {
                toast.success(`${targetUser.name || targetUser.email}'s role updated to ${newRole}`);
                setRefreshTrigger(prev => prev + 1);
            } else {
                throw new Error(result.data.error || 'Failed to update role');
            }
        } catch (err) {
            console.error('Error updating user role:', err);
            toast.error(err.message || 'Failed to update user role');
        }
    };

    // Handle deactivate user
    const handleDeactivateUser = async (targetUser) => {
        if (targetUser.id === user.uid) {
            toast.error("You cannot deactivate yourself");
            return;
        }

        try {
            const firmPortalDeactivateUser = httpsCallable(functions, 'firmPortalDeactivateUser');
            const result = await firmPortalDeactivateUser({
                userId: targetUser.id,
                active: false
            });

            if (result.data.success) {
                toast.success(result.data.message || `${targetUser.name || targetUser.email} has been deactivated`);
                setRefreshTrigger(prev => prev + 1);
            } else {
                throw new Error(result.data.error || 'Failed to deactivate user');
            }
        } catch (err) {
            console.error('Error deactivating user:', err);
            toast.error(err.message || 'Failed to deactivate user');
        }
    };

    // Handle reactivate user
    const handleReactivateUser = async (targetUser) => {
        try {
            const firmPortalDeactivateUser = httpsCallable(functions, 'firmPortalDeactivateUser');
            const result = await firmPortalDeactivateUser({
                userId: targetUser.id,
                active: true
            });

            if (result.data.success) {
                toast.success(result.data.message || `${targetUser.name || targetUser.email} has been reactivated`);
                setRefreshTrigger(prev => prev + 1);
            } else {
                throw new Error(result.data.error || 'Failed to reactivate user');
            }
        } catch (err) {
            console.error('Error reactivating user:', err);
            toast.error(err.message || 'Failed to reactivate user');
        }
    };

    // Format date
    const formatDate = (timestamp) => {
        if (!timestamp?.toDate) return 'Never';
        return timestamp.toDate().toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    // Get role display
    const getRoleChip = (userRecord) => {
    if (userRecord.firmPortalRole === 'admin') {
        return <Chip icon={<AdminIcon />} label="Admin" size="small" color="primary" />;
    }
    if (userRecord.firmPortalRole === 'user') {
        return <Chip icon={<PersonIcon />} label="User" size="small" color="info" variant="outlined" />;
    }
    if (userRecord.userPortalAccess !== false) {
        return <Chip icon={<PersonIcon />} label={fcfoLabel} size="small" color="default" variant="outlined" />;
    }
    return <Chip label="Read-only" size="small" color="default" variant="outlined" />;
};

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading users...</Typography>
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    // Summary stats
    const activeUsers = users.filter(u => u.active !== false);
    const adminCount = users.filter(u => u.firmPortalRole === 'admin').length;
    const fcfoCount = users.filter(u => u.userPortalAccess !== false).length;

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        User Management
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage firm portal access and user roles.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<PersonAddIcon />}
                    onClick={() => setInviteModalOpen(true)}
                >
                    Invite User
                </Button>
            </Box>

            {/* Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">{activeUsers.length}</Typography>
                        <Typography variant="body2" color="text.secondary">Active Users</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">{adminCount}</Typography>
                        <Typography variant="body2" color="text.secondary">Admins</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={4}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">{fcfoCount}</Typography>
                        <Typography variant="body2" color="text.secondary">{fcfosLabel}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* User Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>User</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Clients</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Last Active</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No users found.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((userRecord) => (
                                <TableRow 
                                    key={userRecord.id}
                                    sx={{ 
                                        bgcolor: userRecord.active === false ? 'grey.100' : 'inherit',
                                        opacity: userRecord.active === false ? 0.7 : 1
                                    }}
                                >
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={2}>
                                            <Avatar sx={{ bgcolor: userRecord.firmPortalRole === 'admin' ? 'primary.main' : 'grey.400' }}>
                                                {(userRecord.name || userRecord.email || '?')[0].toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {userRecord.name || 'No name'}
                                                    {userRecord.id === user.uid && (
                                                        <Chip label="You" size="small" sx={{ ml: 1 }} />
                                                    )}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {userRecord.email}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {getRoleChip(userRecord)}
                                    </TableCell>
                                    <TableCell>
                                        <Chip 
                                            icon={<BusinessIcon />}
                                            label={userRecord.clientCount || 0}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {userRecord.active === false ? (
                                            <Chip label="Inactive" size="small" color="default" />
                                        ) : (
                                            <Chip icon={<CheckCircleIcon />} label="Active" size="small" color="success" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(userRecord.lastLoginAt || userRecord.updatedAt)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
    {userRecord.id !== user.uid && (
        <Box display="flex" gap={1} justifyContent="flex-end">
            {/* Role Management - Context-aware options */}
            
            {/* Admin: Can demote to User */}
            {userRecord.firmPortalRole === 'admin' && (
                <Tooltip title="Demote to User">
                    <IconButton 
                        size="small"
                        onClick={() => handleUpdateRole(userRecord, 'user')}
                    >
                        <PersonIcon />
                    </IconButton>
                </Tooltip>
            )}
            
            {/* User (FP access, not admin): Can promote to Admin or remove FP access */}
            {userRecord.firmPortalRole === 'user' && (
                <>
                    <Tooltip title="Promote to Admin">
                        <IconButton 
                            size="small"
                            color="primary"
                            onClick={() => handleUpdateRole(userRecord, 'admin')}
                        >
                            <AdminIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove Firm Portal Access">
                        <IconButton 
                            size="small"
                            color="warning"
                            onClick={() => handleUpdateRole(userRecord, null)}
                        >
                            <PersonOffIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )}
            
            {/* FCFO (no FP access): Can grant User or Admin access */}
            {!userRecord.firmPortalRole && userRecord.userPortalAccess !== false && (
                <>
                    <Tooltip title="Grant Firm Portal Access (User)">
                        <IconButton 
                            size="small"
                            color="info"
                            onClick={() => handleUpdateRole(userRecord, 'user')}
                        >
                            <PersonIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Grant Firm Portal Access (Admin)">
                        <IconButton 
                            size="small"
                            color="primary"
                            onClick={() => handleUpdateRole(userRecord, 'admin')}
                        >
                            <AdminIcon />
                        </IconButton>
                    </Tooltip>
                </>
            )}
            
            {/* Activate/Deactivate - unchanged */}
            {userRecord.active === false ? (
                <Tooltip title="Reactivate User">
                    <IconButton 
                        size="small"
                        color="success"
                        onClick={() => handleReactivateUser(userRecord)}
                    >
                        <CheckCircleIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Deactivate User">
                    <IconButton 
                        size="small"
                        color="error"
                        onClick={() => handleDeactivateUser(userRecord)}
                    >
                        <BlockIcon />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    )}
</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Invite User Modal */}
            <Dialog open={inviteModalOpen} onClose={() => setInviteModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Invite User to Firm Portal</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Email Address"
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            fullWidth
                            required
                            helperText="User will receive an invitation email"
                        />
                        <TextField
                            label="Name (Optional)"
                            value={inviteName}
                            onChange={(e) => setInviteName(e.target.value)}
                            fullWidth
                        />
                        <FormControl fullWidth>
    <InputLabel>Portal Role</InputLabel>
    <Select
        value={inviteRole}
        label="Portal Role"
        onChange={(e) => setInviteRole(e.target.value)}
    >
        <MenuItem value="user">User (Full access, no User Management)</MenuItem>
        <MenuItem value="admin">Admin (Full access + User Management)</MenuItem>
    </Select>
    <FormHelperText>
        {inviteRole === 'admin' 
            ? 'Admins have full access plus can invite users, change roles, and deactivate users'
            : 'Users have full access to Dashboard, Activity, Clients, and Analytics'
        }
    </FormHelperText>
</FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInviteModalOpen(false)} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleInviteUser} 
                        variant="contained" 
                        disabled={isSubmitting || !inviteEmail.trim()}
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : 'Send Invitation'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FirmPortalUsers;